import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { BarChart3, Users, Lightbulb, Download, TrendingUp, ArrowLeft, Shield, Trash2, UserCheck, DollarSign, CreditCard, Mail, Megaphone, Gift, Share2, ToggleLeft, ToggleRight, Globe, Tag, Activity, MessageSquare, Send, ChevronDown, ChevronUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailStats, setEmailStats] = useState(null);
  const [referralStats, setReferralStats] = useState({ total_codes: 0, total_uses: 0 });
  const [waitlistEnabled, setWaitlistEnabled] = useState(true);
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [trends, setTrends] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, emailRes, settingsRes, wlCountRes, trendsRes, ticketsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics/stats`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/users`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/marketing/email-stats`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/settings/public`).catch(() => ({ data: { waitlist_enabled: true } })),
        axios.get(`${BACKEND_URL}/api/waitlist/count`).catch(() => ({ data: { count: 0 } })),
        axios.get(`${BACKEND_URL}/api/admin/analytics/idea-trends`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/support/tickets`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      if (emailRes.data) setEmailStats(emailRes.data);
      setWaitlistEnabled(settingsRes.data.waitlist_enabled !== false);
      setWaitlistCount(wlCountRes.data.count || 0);
      if (trendsRes.data) setTrends(trendsRes.data);
      setTickets(Array.isArray(ticketsRes.data) ? ticketsRes.data : []);

      const usersWithRef = usersRes.data.filter(u => u.referral_code);
      setReferralStats({ total_codes: usersWithRef.length, total_uses: 0 });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const promoteToAdmin = async (userId) => {
    if (!window.confirm('Are you sure you want to promote this user to admin?')) return;
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${userId}/role`,
        { role: 'admin' },
        { withCredentials: true }
      );
      await fetchData();
      alert('User promoted to admin successfully!');
    } catch (error) {
      alert('Failed to promote user: ' + (error.response?.data?.detail || error.message));
    }
  };

  const demoteToUser = async (userId) => {
    if (!window.confirm('Are you sure you want to demote this admin to regular user?')) return;
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${userId}/role`,
        { role: 'user' },
        { withCredentials: true }
      );
      await fetchData();
      alert('User demoted successfully!');
    } catch (error) {
      alert('Failed to demote user: ' + (error.response?.data?.detail || error.message));
    }
  };

  const toggleWaitlist = async () => {
    try {
      const newVal = !waitlistEnabled;
      await axios.put(`${BACKEND_URL}/api/settings`, { waitlist_enabled: newVal }, { withCredentials: true });
      setWaitlistEnabled(newVal);
    } catch (err) {
      alert('Failed to update settings');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This will also delete all their ideas.')) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/users/${userId}`,
        { withCredentials: true }
      );
      await fetchData();
      alert('User deleted successfully!');
    } catch (error) {
      alert('Failed to delete user: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--main-bg)] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[var(--obsidian)] border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--main-bg)] p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--obsidian)] mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Back to Dashboard</span>
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                ADMIN DASHBOARD
              </h1>
              <p className="text-sm sm:text-base text-[var(--text-secondary)]">
                Analytics & User Management
              </p>
            </div>
            <div className="bg-[var(--primary)] text-white px-4 py-2 border-2 border-[var(--border-default)]">
              <span className="text-xs font-mono uppercase tracking-wider">Admin</span>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          {/* Total PWA Installs */}
          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#007AFF]"></div>
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-[#007AFF]/10 border border-[#007AFF]">
                <Download size={20} className="text-[#007AFF]" />
              </div>
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)]">PWA Installs</span>
            </div>
            <div className="text-5xl font-black tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {stats?.total_pwa_installs || 0}
            </div>
            <p className="text-xs text-[var(--text-secondary)] mt-2">
              Total app installations
            </p>
          </div>

          {/* Total Users */}
          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#FF3B30]"></div>
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-[#FF3B30]/10 border border-[#FF3B30]">
                <Users size={20} className="text-[#FF3B30]" />
              </div>
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)]">Total Users</span>
            </div>
            <div className="text-5xl font-black tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {stats?.total_users || 0}
            </div>
            <p className="text-xs text-[var(--text-secondary)] mt-2">
              Registered accounts
            </p>
          </div>

          {/* Total Ideas */}
          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#FFD60A]"></div>
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-[#FFD60A]/10 border border-[#FFD60A]">
                <Lightbulb size={20} className="text-[#FFD60A]" />
              </div>
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)]">Total Ideas</span>
            </div>
            <div className="text-5xl font-black tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {stats?.total_ideas || 0}
            </div>
            <p className="text-xs text-[var(--text-secondary)] mt-2">
              Ideas created
            </p>
          </div>

          {/* Ideas per User */}
          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#34C759]"></div>
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-[#34C759]/10 border border-[#34C759]">
                <TrendingUp size={20} className="text-[#34C759]" />
              </div>
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)]">Avg/User</span>
            </div>
            <div className="text-5xl font-black tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {stats?.total_users > 0 ? Math.round((stats?.total_ideas || 0) / stats?.total_users) : 0}
            </div>
            <p className="text-xs text-[var(--text-secondary)] mt-2">
              Ideas per user
            </p>
          </div>
        </div>

        {/* Revenue & Subscription Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          {/* Total Revenue */}
          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#34C759]"></div>
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-[#34C759]/10 border border-[#34C759]">
                <DollarSign size={20} className="text-[#34C759]" />
              </div>
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)]">Total Revenue</span>
            </div>
            <div className="text-4xl font-black tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              ${stats?.total_revenue || 0}
            </div>
            <p className="text-xs text-[var(--text-secondary)] mt-2">
              From {stats?.total_transactions || 0} transactions
            </p>
          </div>

          {/* Pro Subscriptions */}
          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#007AFF]"></div>
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-[#007AFF]/10 border border-[#007AFF]">
                <CreditCard size={20} className="text-[#007AFF]" />
              </div>
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)]">Pro Subs</span>
            </div>
            <div className="text-4xl font-black tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {stats?.pro_subscriptions || 0}
            </div>
            <p className="text-xs text-[var(--text-secondary)] mt-2">
              Active Pro subscriptions
            </p>
          </div>

          {/* Business Subscriptions */}
          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#FF3B30]"></div>
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-[#FF3B30]/10 border border-[#FF3B30]">
                <Users size={20} className="text-[#FF3B30]" />
              </div>
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)]">Business Subs</span>
            </div>
            <div className="text-4xl font-black tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {stats?.business_subscriptions || 0}
            </div>
            <p className="text-xs text-[var(--text-secondary)] mt-2">
              Active Business plans
            </p>
          </div>

          {/* Founding Members */}
          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#FFD60A]"></div>
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-[#FFD60A]/10 border border-[#FFD60A]">
                <Shield size={20} className="text-[#FFD60A]" />
              </div>
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)]">Founding</span>
            </div>
            <div className="text-4xl font-black tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {stats?.founding_members || 0}
            </div>
            <p className="text-xs text-[var(--text-secondary)] mt-2">
              Lifetime members
            </p>
          </div>
        </div>

        {/* Engagement Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Install Rate */}
          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6">
            <h3 className="text-xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              PWA Install Rate
            </h3>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-[var(--text-secondary)]">Installs vs Users</span>
                  <span className="text-sm font-bold">
                    {stats?.total_users > 0 
                      ? Math.round((stats?.total_pwa_installs / stats?.total_users) * 100)
                      : 0}%
                  </span>
                </div>
                <div className="w-full h-8 bg-[var(--surface-alt)] border border-[var(--border-default)]">
                  <div 
                    className="h-full bg-gradient-to-r from-[#007AFF] to-[#0055CC] border-r border-[var(--border-default)] transition-all duration-500"
                    style={{ 
                      width: `${stats?.total_users > 0 
                        ? Math.min(100, Math.round((stats?.total_pwa_installs / stats?.total_users) * 100))
                        : 0}%`
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* User Engagement */}
          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6">
            <h3 className="text-xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              User Engagement
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--text-secondary)]">Active Users</span>
                <span className="text-2xl font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {stats?.total_users || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--text-secondary)]">Total Ideas Created</span>
                <span className="text-2xl font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {stats?.total_ideas || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--text-secondary)]">PWA Adoption</span>
                <span className="text-2xl font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {stats?.total_pwa_installs || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Waitlist Control */}
        <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 mb-8" data-testid="waitlist-control">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Globe size={20} className="text-[var(--primary)]" />
              <div>
                <h3 className="font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Waitlist Landing Page</h3>
                <p className="text-sm text-[var(--text-secondary)]">
                  {waitlistEnabled ? `Active - ${waitlistCount} signups collected` : 'Disabled - visitors go straight to login'}
                </p>
              </div>
            </div>
            <button
              onClick={toggleWaitlist}
              data-testid="toggle-waitlist-btn"
              className="flex items-center gap-2 px-4 py-2 border-2 border-[var(--border-default)] font-bold text-sm hover:bg-[var(--surface-alt)] transition-colors"
            >
              {waitlistEnabled ? <><ToggleRight size={20} className="text-green-500" /> ON</> : <><ToggleLeft size={20} className="text-[var(--text-muted)]" /> OFF</>}
            </button>
          </div>
        </div>

        {/* Marketing Performance Widget */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" data-testid="marketing-performance-widget">
          {/* Email Stats */}
          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6">
            <div className="flex items-center gap-2 mb-4">
              <Mail size={20} className="text-[var(--secondary)]" />
              <h3 className="text-lg font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Email Performance</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--text-secondary)]">Total Sent</span>
                <span className="font-black text-xl">{emailStats?.sent || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--text-secondary)]">Queued</span>
                <span className="font-black text-xl text-[var(--secondary)]">{emailStats?.queued || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--text-secondary)]">Failed</span>
                <span className="font-black text-xl text-[var(--primary)]">{emailStats?.failed || 0}</span>
              </div>
            </div>
          </div>

          {/* Referral Stats */}
          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6">
            <div className="flex items-center gap-2 mb-4">
              <Gift size={20} className="text-[#FFD60A]" />
              <h3 className="text-lg font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Referral Program</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--text-secondary)]">Active Codes</span>
                <span className="font-black text-xl">{referralStats.total_codes}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--text-secondary)]">Conversion Rate</span>
                <span className="font-black text-xl">{stats?.total_users > 0 ? Math.round((referralStats.total_codes / stats.total_users) * 100) : 0}%</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6">
            <div className="flex items-center gap-2 mb-4">
              <Megaphone size={20} className="text-[var(--primary)]" />
              <h3 className="text-lg font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Quick Actions</h3>
            </div>
            <div className="space-y-2">
              <a
                href="/marketing"
                className="w-full flex items-center gap-2 px-4 py-2.5 bg-[var(--obsidian)] text-white text-sm font-bold border-2 border-[var(--border-default)] hover:translate-x-[2px] hover:translate-y-[2px] transition-transform"
              >
                <Megaphone size={14} /> Open Marketing Hub
              </a>
              <a
                href="/pricing"
                className="w-full flex items-center gap-2 px-4 py-2.5 bg-[var(--secondary)] text-white text-sm font-bold border-2 border-[var(--border-default)] hover:opacity-90 transition-opacity"
              >
                <CreditCard size={14} /> View Pricing Page
              </a>
            </div>
          </div>
        </div>

        {/* In-App Analytics & Idea Trends */}
        {trends && (
          <div className="mb-8" data-testid="idea-trends-section">
            <div className="flex items-center gap-2 mb-6">
              <Activity size={24} className="text-[var(--secondary)]" />
              <h3 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Idea Trends & Analytics
              </h3>
            </div>

            {/* Idea Activity Chart (last 30 days) */}
            <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 mb-6">
              <h4 className="text-sm font-mono uppercase tracking-wider text-[var(--text-secondary)] mb-4">Ideas Created — Last 30 Days</h4>
              <div className="flex items-end gap-[2px] h-32" data-testid="ideas-chart">
                {trends.daily_ideas.map((d, i) => {
                  const maxCount = Math.max(...trends.daily_ideas.map(x => x.count), 1);
                  const height = d.count > 0 ? Math.max(8, (d.count / maxCount) * 100) : 2;
                  return (
                    <div key={`bar-${i}`} className="flex-1 flex flex-col items-center justify-end group relative">
                      <div
                        className="w-full bg-[#007AFF] hover:bg-[#0055CC] transition-colors cursor-pointer min-w-[4px]"
                        style={{ height: `${height}%` }}
                        title={`${d.date}: ${d.count} ideas`}
                      />
                      <div className="hidden group-hover:block absolute -top-8 bg-[var(--obsidian)] text-white text-[10px] px-2 py-1 whitespace-nowrap z-10">
                        {d.date.slice(5)}: {d.count}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-[10px] text-[var(--text-muted)]">{trends.daily_ideas[0]?.date?.slice(5)}</span>
                <span className="text-[10px] text-[var(--text-muted)]">{trends.daily_ideas[trends.daily_ideas.length - 1]?.date?.slice(5)}</span>
              </div>
            </div>

            {/* User Signups Chart */}
            <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 mb-6">
              <h4 className="text-sm font-mono uppercase tracking-wider text-[var(--text-secondary)] mb-4">User Signups — Last 30 Days</h4>
              <div className="flex items-end gap-[2px] h-24" data-testid="signups-chart">
                {trends.daily_signups.map((d, i) => {
                  const maxCount = Math.max(...trends.daily_signups.map(x => x.count), 1);
                  const height = d.count > 0 ? Math.max(8, (d.count / maxCount) * 100) : 2;
                  return (
                    <div key={`bar-${i}`} className="flex-1 flex flex-col items-center justify-end group relative">
                      <div
                        className="w-full bg-[#34C759] hover:bg-[#2DA04A] transition-colors cursor-pointer min-w-[4px]"
                        style={{ height: `${height}%` }}
                        title={`${d.date}: ${d.count} signups`}
                      />
                      <div className="hidden group-hover:block absolute -top-8 bg-[var(--obsidian)] text-white text-[10px] px-2 py-1 whitespace-nowrap z-10">
                        {d.date.slice(5)}: {d.count}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-[10px] text-[var(--text-muted)]">{trends.daily_signups[0]?.date?.slice(5)}</span>
                <span className="text-[10px] text-[var(--text-muted)]">{trends.daily_signups[trends.daily_signups.length - 1]?.date?.slice(5)}</span>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Category Distribution */}
              <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6">
                <h4 className="text-sm font-mono uppercase tracking-wider text-[var(--text-secondary)] mb-4">Category Distribution</h4>
                {Object.keys(trends.category_distribution).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(trends.category_distribution)
                      .sort((a, b) => b[1] - a[1])
                      .map(([cat, count]) => {
                        const total = Object.values(trends.category_distribution).reduce((a, b) => a + b, 0);
                        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                        return (
                          <div key={cat}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="font-medium">{cat}</span>
                              <span className="text-[var(--text-secondary)]">{count} ({pct}%)</span>
                            </div>
                            <div className="w-full h-2 bg-[var(--surface-alt)] border border-[var(--border-subtle)]">
                              <div className="h-full bg-[#FF3B30]" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <p className="text-sm text-[var(--text-muted)]">No data yet</p>
                )}
              </div>

              {/* Top Tags */}
              <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6">
                <h4 className="text-sm font-mono uppercase tracking-wider text-[var(--text-secondary)] mb-4">Top Tags</h4>
                {trends.top_tags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {trends.top_tags.map((t) => (
                      <span
                        key={t.tag}
                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-[var(--border-default)] text-xs font-mono"
                      >
                        <Tag size={10} />
                        {t.tag}
                        <span className="ml-1 text-[var(--text-muted)]">{t.count}</span>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[var(--text-muted)]">No tags yet</p>
                )}
              </div>

              {/* Active vs Archived */}
              <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6">
                <h4 className="text-sm font-mono uppercase tracking-wider text-[var(--text-secondary)] mb-4">Ideas Status</h4>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Active</span>
                    <span className="text-2xl font-black text-[#34C759]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      {trends.active_ideas}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Archived</span>
                    <span className="text-2xl font-black text-[var(--text-muted)]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      {trends.archived_ideas}
                    </span>
                  </div>
                  <div className="w-full h-4 bg-[var(--surface-alt)] border border-[var(--border-subtle)] flex overflow-hidden">
                    <div className="h-full bg-[#34C759]" style={{ width: `${(trends.active_ideas + trends.archived_ideas) > 0 ? (trends.active_ideas / (trends.active_ideas + trends.archived_ideas)) * 100 : 0}%` }} />
                    <div className="h-full bg-[var(--text-muted)]" style={{ width: `${(trends.active_ideas + trends.archived_ideas) > 0 ? (trends.archived_ideas / (trends.active_ideas + trends.archived_ideas)) * 100 : 0}%` }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Top Users by Ideas */}
            {trends.top_users.length > 0 && (
              <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6">
                <h4 className="text-sm font-mono uppercase tracking-wider text-[var(--text-secondary)] mb-4">Top Users by Ideas</h4>
                <div className="space-y-2">
                  {trends.top_users.map((u, i) => (
                    <div key={u.name || `user-${i}`} className="flex items-center justify-between py-2 border-b border-[var(--border-subtle)] last:border-0">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 flex items-center justify-center bg-[var(--obsidian)] text-white text-xs font-bold">
                          {i + 1}
                        </span>
                        <span className="text-sm font-medium">{u.name}</span>
                      </div>
                      <span className="text-sm font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                        {u.count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* User Management Section */}
        <div className="mb-8 bg-[var(--surface)] border-2 border-[var(--border-default)] p-6">
          <div className="flex items-center gap-2 mb-6">
            <Users size={24} />
            <h3 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              User Management
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-[var(--border-default)]">
                  <th className="text-left p-3 text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)]">Name</th>
                  <th className="text-left p-3 text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)]">Email</th>
                  <th className="text-left p-3 text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)]">Role</th>
                  <th className="text-left p-3 text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)]">Ideas</th>
                  <th className="text-left p-3 text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u._id} className="border-b border-[var(--border-subtle)] hover:bg-[var(--surface-alt)]">
                    <td className="p-3">
                      <span className="font-medium">{u.name || 'N/A'}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-[var(--text-secondary)]">{u.email}</span>
                    </td>
                    <td className="p-3">
                      <span className={`inline-block px-2 py-1 border text-xs font-mono uppercase tracking-wider ${
                        u.role === 'admin' 
                          ? 'bg-[var(--primary)] text-white border-[var(--primary)]' 
                          : 'bg-[var(--surface)] border-[var(--border-default)]'
                      }`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm font-bold">{u.idea_count || 0}</span>
                    </td>
                    <td className="p-3">
                      {u._id !== user._id && (
                        <div className="flex gap-2">
                          {u.role === 'admin' ? (
                            <button
                              onClick={() => demoteToUser(u._id)}
                              className="px-3 py-1 border-2 border-[var(--border-default)] text-xs font-bold hover:bg-[var(--surface-alt)] transition-colors"
                              title="Demote to User"
                            >
                              Demote
                            </button>
                          ) : (
                            <button
                              onClick={() => promoteToAdmin(u._id)}
                              className="px-3 py-1 bg-[var(--primary)] text-white border-2 border-[var(--border-default)] text-xs font-bold hover:opacity-90 transition-opacity flex items-center gap-1"
                              title="Promote to Admin"
                            >
                              <Shield size={14} />
                              Promote
                            </button>
                          )}
                          <button
                            onClick={() => deleteUser(u._id)}
                            className="px-3 py-1 border-2 border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-white text-xs font-bold transition-colors flex items-center gap-1"
                            title="Delete User"
                          >
                            <Trash2 size={14} />
                            Delete
                          </button>
                        </div>
                      )}
                      {u._id === user._id && (
                        <span className="text-xs text-[var(--text-secondary)] italic">You</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {users.length === 0 && (
            <div className="text-center py-8 text-[var(--text-secondary)]">
              No users found
            </div>
          )}
        </div>

        {/* Support Tickets Section */}
        <div className="mb-8 bg-[var(--surface)] border-2 border-[var(--border-default)] p-6" data-testid="admin-tickets-section">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <MessageSquare size={24} />
              <h3 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Support Tickets
              </h3>
              {tickets.filter(t => t.status === 'open').length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-[var(--primary)] text-white text-xs font-bold border border-[var(--border-default)]">
                  {tickets.filter(t => t.status === 'open').length} open
                </span>
              )}
            </div>
          </div>

          {tickets.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">No support tickets yet.</p>
          ) : (
            <div className="space-y-3">
              {tickets.map(ticket => {
                const isOpen = selectedTicket?._id === ticket._id;
                const statusColors = {
                  open: 'bg-[var(--primary)] text-white',
                  in_progress: 'bg-[var(--warning)] text-[#0A0A0A]',
                  resolved: 'bg-green-600 text-white',
                  closed: 'bg-[var(--text-muted)] text-white'
                };
                return (
                  <div key={ticket._id} className="border border-[var(--border-default)]">
                    <button
                      data-testid={`ticket-${ticket._id}`}
                      onClick={async () => {
                        if (isOpen) { setSelectedTicket(null); return; }
                        try {
                          const { data } = await axios.get(`${BACKEND_URL}/api/support/tickets/${ticket._id}`, { withCredentials: true });
                          setSelectedTicket(data);
                          setReplyText('');
                        } catch { setSelectedTicket(ticket); }
                      }}
                      className="w-full flex items-center justify-between p-3 hover:bg-[var(--surface-alt)] transition-colors text-left"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-mono text-[var(--text-muted)]">#{ticket.number}</span>
                          <span className="text-sm font-semibold truncate">{ticket.subject}</span>
                        </div>
                        <div className="text-xs text-[var(--text-muted)] mt-0.5">
                          {ticket.user_email || 'Unknown'} &middot; {ticket.category} &middot; {ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : ''}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase border border-[var(--border-default)] ${statusColors[ticket.status] || ''}`}>
                          {ticket.status?.replace('_', ' ')}
                        </span>
                        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </div>
                    </button>

                    {isOpen && selectedTicket && (
                      <div className="border-t border-[var(--border-subtle)] p-4 space-y-4">
                        {/* Sender info */}
                        <div className="flex items-center gap-3 text-xs text-[var(--text-muted)]">
                          <span className="font-bold">From:</span>
                          <span>{selectedTicket.user_name || 'Unknown'}</span>
                          <span className="font-mono text-[var(--secondary)]">{selectedTicket.user_email}</span>
                          <span>&middot;</span>
                          <span>{selectedTicket.category}</span>
                          <span>&middot;</span>
                          <span>{selectedTicket.created_at ? new Date(selectedTicket.created_at).toLocaleString() : ''}</span>
                        </div>

                        {/* Subject */}
                        <div className="text-sm font-bold text-[var(--text-primary)]">
                          {selectedTicket.subject}
                        </div>

                        {/* Full message body */}
                        <div className="text-sm text-[var(--text-secondary)] bg-[var(--surface-alt)] p-4 border border-[var(--border-subtle)] whitespace-pre-wrap leading-relaxed">
                          {selectedTicket.description}
                        </div>

                        {/* Replies */}
                        {selectedTicket.replies?.length > 0 && (
                          <div className="space-y-2">
                            {selectedTicket.replies.map((r, i) => (
                              <div key={`reply-${i}`} className={`p-3 text-sm border ${r.is_admin ? 'border-[var(--secondary)] bg-[var(--secondary)]/5' : 'border-[var(--border-subtle)]'}`}>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-bold">{r.author_name}</span>
                                  {r.is_admin && <span className="text-[10px] px-1 bg-[var(--secondary)] text-white font-bold">ADMIN</span>}
                                  <span className="text-[10px] text-[var(--text-muted)]">{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</span>
                                </div>
                                <p className="text-[var(--text-secondary)]">{r.message}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Reply + Status */}
                        <div className="flex flex-col sm:flex-row gap-2">
                          <input
                            data-testid={`reply-input-${ticket._id}`}
                            type="text"
                            placeholder="Write a reply..."
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            onKeyDown={async (e) => {
                              if (e.key === 'Enter' && replyText.trim()) {
                                setSendingReply(true);
                                try {
                                  await axios.post(`${BACKEND_URL}/api/support/tickets/${ticket._id}/reply`, { message: replyText }, { withCredentials: true });
                                  const { data } = await axios.get(`${BACKEND_URL}/api/support/tickets/${ticket._id}`, { withCredentials: true });
                                  setSelectedTicket(data);
                                  setReplyText('');
                                } catch {} finally { setSendingReply(false); }
                              }
                            }}
                            className="flex-1 px-3 py-2 text-sm border border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-primary)]"
                          />
                          <button
                            data-testid={`reply-btn-${ticket._id}`}
                            disabled={sendingReply || !replyText.trim()}
                            onClick={async () => {
                              if (!replyText.trim()) return;
                              setSendingReply(true);
                              try {
                                await axios.post(`${BACKEND_URL}/api/support/tickets/${ticket._id}/reply`, { message: replyText }, { withCredentials: true });
                                const { data } = await axios.get(`${BACKEND_URL}/api/support/tickets/${ticket._id}`, { withCredentials: true });
                                setSelectedTicket(data);
                                setReplyText('');
                              } catch {} finally { setSendingReply(false); }
                            }}
                            className="px-4 py-2 text-xs font-bold bg-[var(--secondary)] text-white border border-[var(--border-default)] disabled:opacity-50 flex items-center gap-1"
                          >
                            <Send size={12} /> Reply
                          </button>
                          <select
                            data-testid={`ticket-status-select-${ticket._id}`}
                            value={selectedTicket.status}
                            onChange={async (e) => {
                              const newStatus = e.target.value;
                              try {
                                await axios.put(`${BACKEND_URL}/api/support/tickets/${ticket._id}/status`, { status: newStatus }, { withCredentials: true });
                                setSelectedTicket(prev => ({ ...prev, status: newStatus }));
                                fetchData();
                              } catch {}
                            }}
                            className="px-3 py-2 text-xs font-bold border border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-primary)]"
                          >
                            <option value="open">Open</option>
                            <option value="in_progress">In Progress</option>
                            <option value="resolved">Resolved</option>
                            <option value="closed">Closed</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>


        {/* Info Panel */}
        <div className="bg-gradient-to-br from-[#007AFF]/10 to-[#FFD60A]/10 border-2 border-[var(--border-default)] p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-[var(--obsidian)] border-2 border-[var(--border-default)]">
              <BarChart3 size={24} className="text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Analytics Tracking Active
              </h3>
              <p className="text-sm text-[var(--text-secondary)] mb-2">
                VAULT is tracking PWA installations and user engagement in real-time.
              </p>
              <ul className="text-sm text-[var(--text-secondary)] space-y-1">
                <li>• PWA install events are tracked automatically</li>
                <li>• User registrations are counted</li>
                <li>• Idea creation metrics are recorded</li>
                <li>• All data updates in real-time</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Deployment Note */}
        <div className="mt-6 bg-[var(--surface)] border-2 border-[var(--border-default)] p-6">
          <h4 className="text-sm font-bold mb-2 uppercase tracking-wider">Deployment Note</h4>
          <p className="text-sm text-[var(--text-secondary)]">
            Once deployed to a custom domain, PWA installations will work seamlessly. Users will see the VAULT icon on their home screen. The preview URL has some limitations, but all tracking is working correctly and ready for production!
          </p>
        </div>
      </div>
    </div>
  );
};


export default AdminDashboard;

